/** @jsx jsx */
import { Icon } from "@cd2/components"
import { css, jsx } from "@emotion/core"
import { IComponentProp } from "@cd2/tools"
import React from "react"
import { nd } from "./tokens"

/**
 * A vertical activity timeline, newest first, as at the bottom of a Shopify order. Group items by
 * day with `Timeline.Group`; each `Timeline.Item` has a small icon on the rail, a short
 * summary (wraps if long) with who / when right after it, and optional detail underneath.
 */
export function Timeline({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        display: grid;
        gap: ${nd.space.xl}px;
      `}
    >
      {children}
    </div>
  )
}

/** A day (or other period) of timeline items under a small grey heading. */
function TimelineGroup({
  heading,
  children,
}: {
  heading: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section>
      <h3
        css={css`
          margin: 0 0 ${nd.space.sm}px;
          padding-left: 36px;
          font-size: ${nd.fontSize.small}px;
          font-weight: ${nd.fontWeight.semibold};
          line-height: 16px;
          color: ${nd.color.textSubdued};
        `}
      >
        {heading}
      </h3>
      <ol
        css={css`
          margin: 0;
          padding: 0;
          list-style: none;
        `}
      >
        {children}
      </ol>
    </section>
  )
}

/**
 * One event on the timeline. `iconColor` tints the icon to hint at the kind of event (green for
 * created, red for errors); the rail and circle stay neutral so a long log stays calm.
 */
function TimelineItem({
  icon,
  iconColor,
  title,
  meta,
  time,
  children,
}: {
  icon?: IComponentProp<typeof Icon, "icon">
  iconColor?: string
  /** The short summary, e.g. "Purchase order exported to Sage" */
  title: React.ReactNode
  /** Who did it, shown after the time, right beside the title */
  meta?: React.ReactNode
  /** When it happened, e.g. "14:32" */
  time?: React.ReactNode
  /** Extra detail under the summary: changed values, a note, raw data */
  children?: React.ReactNode
}) {
  return (
    <li
      css={css`
        position: relative;
        display: grid;
        grid-template-columns: 24px minmax(0, 1fr);
        column-gap: ${nd.space.md}px;
        padding-bottom: ${nd.space.lg}px;

        /* the rail joining this item to the next */
        &:not(:last-of-type)::before {
          content: "";
          position: absolute;
          left: 11px;
          top: 28px;
          bottom: 4px;
          width: 2px;
          border-radius: 1px;
          background: ${nd.color.border};
        }
        &:last-of-type {
          padding-bottom: 0;
        }
      `}
    >
      <span
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${nd.color.surfaceSubdued};
          box-shadow: inset 0 0 0 1px ${nd.color.border};
          color: ${iconColor ?? nd.color.textSubdued};
          font-size: 11px;
        `}
      >
        {icon && <Icon icon={icon} fixedWidth />}
      </span>
      <div
        css={css`
          min-width: 0;
          padding-top: 2px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            column-gap: ${nd.space.md}px;
            row-gap: 2px;
          `}
        >
          <div
            css={css`
              flex: 0 1 auto;
              min-width: 0;
              overflow-wrap: anywhere;
            `}
          >
            {title}
          </div>
          {(time || meta) && (
            <div
              css={css`
                flex: none;
                font-size: ${nd.fontSize.small}px;
                line-height: 16px;
                color: ${nd.color.textSubdued};
              `}
            >
              {time}
              {time && meta && " · "}
              {meta}
            </div>
          )}
        </div>
        {children && (
          <div
            css={css`
              margin-top: ${nd.space.xs}px;
              color: ${nd.color.textSubdued};
              overflow-wrap: anywhere;
            `}
          >
            {children}
          </div>
        )}
      </div>
    </li>
  )
}

Timeline.Group = TimelineGroup
Timeline.Item = TimelineItem
